export default function BookDetailView({
  active,
  fromNextUp,
  dFav,
  dStatus,
  dFmts,
  dGenres,
  dVibes,
  dTags,
  dNotes,
  dReads,
  dRereading,
  dRereadStartDate,
  dRating,
  dSeriesName,
  dSeriesNum,
  dEdition,
  dStartDate,
  dEndDate,
  T,
  SANS,
  DISPLAY_SERIF,
  STATUS,
  BookCover,
  HalfStars,
  detailViewScrollRef,
  formatBookDate,
  readingDays,
  cleanBookDescription,
  activePublishedDisplay,
  activeHasPages,
  showStartReadingButton,
  startReadingButtonLabel,
  onBack,
  onToggleFavorite,
  onEdit,
  onStartReading,
  onFinishReading,
  onDelete,
  onReportMetadata,
}) {
  const dividerColor = T.border || "rgba(0,0,0,0.08)";
  const themeAccent = T.accentStrong || T.accent || T.text;
  const sectionLabelColor = themeAccent !== T.text ? themeAccent : T.sub;
  const eyebrowColor = themeAccent !== T.text ? themeAccent : T.mute;
  const seriesLabelColor = T.accent || eyebrowColor;
  const noteRuleColor = themeAccent !== T.text ? themeAccent : dividerColor;
  const withAlpha = (color, alpha) => {
    if (!color) return `rgba(0,0,0,${alpha})`;
    if (color.startsWith("#")) {
      const hex = color.slice(1);
      const normalized =
        hex.length === 3
          ? hex.split("").map((ch) => ch + ch).join("")
          : hex;
      if (normalized.length === 6) {
        const r = parseInt(normalized.slice(0, 2), 16);
        const g = parseInt(normalized.slice(2, 4), 16);
        const b = parseInt(normalized.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${alpha})`;
      }
    }
    const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
      const parts = rgbMatch[1].split(",").map((part) => part.trim());
      return `rgba(${parts[0]},${parts[1]},${parts[2]},${alpha})`;
    }
    return color;
  };
  const getHexLuminance = (color) => {
    if (!color || !color.startsWith("#")) return 0;
    const hex = color.slice(1);
    const normalized =
      hex.length === 3
        ? hex.split("").map((ch) => ch + ch).join("")
        : hex;
    if (normalized.length !== 6) return 0;
    const values = [0, 2, 4].map((idx) => parseInt(normalized.slice(idx, idx + 2), 16) / 255);
    const [r, g, b] = values.map((value) =>
      value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4,
    );
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const isDarkTheme = getHexLuminance(T.bg) < 0.28;
  const heroArtOpacity = active?.cover ? (isDarkTheme ? 0.46 : 0.3) : 0;
  const heroGradient = `radial-gradient(120% 88% at 50% 0%, ${withAlpha(themeAccent, isDarkTheme ? 0.22 : 0.14)} 0%, ${withAlpha(T.accent || themeAccent, isDarkTheme ? 0.12 : 0.08)} 28%, transparent 62%), linear-gradient(180deg, ${withAlpha(T.bg, isDarkTheme ? 0.12 : 0.06)} 0%, ${withAlpha(T.s2, isDarkTheme ? 0.42 : 0.28)} 24%, ${withAlpha(T.surface, isDarkTheme ? 0.76 : 0.64)} 50%, ${withAlpha(T.bg, 0.95)} 76%, ${T.bg} 100%)`;
  const outlineButtonStyle = {
    flexShrink: 0,
    padding: "11px 16px",
    background: "transparent",
    border: "1px solid " + (T.border || themeAccent),
    borderRadius: 14,
    color: T.text,
    fontSize: 15,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: DISPLAY_SERIF,
    textTransform: "capitalize",
  };
  const aboutText = active.desc ? cleanBookDescription(active.desc) : "";
  const daysRead = readingDays(dStartDate, dEndDate);
  const statusLabel = dRereading ? "Rereading" : STATUS[dStatus]?.label || dStatus || "";
  const formatLabels = (dFmts || []).filter(Boolean);
  const statusTone = STATUS[dStatus]?.color || sectionLabelColor;
  const genreTone = T.text;
  const vibeTone = T.sub || T.text;
  const tagTone = T.accent || themeAccent || T.text;
  const separatorTone = T.mute || T.border || T.sub || T.text;
  const tagItems = [
    ...(dGenres || []).filter(Boolean).map((label) => ({
      label,
      kind: "genre",
    })),
    ...(dVibes || []).filter(Boolean).map((label) => ({
      label,
      kind: "vibe",
    })),
    ...(dTags || []).filter(Boolean).map((label) => ({
      label,
      kind: "tag",
    })),
  ];
  const visibleTagItems = tagItems;
  const showInlineFinishButton =
    !dRereading && dStatus === "In Progress" && !!dStartDate && !dEndDate;
  const showInlineRereadFinishButton = !!(dRereading && dRereadStartDate);
  const showPrimaryButton =
    showStartReadingButton && !showInlineFinishButton && !showInlineRereadFinishButton;

  const readingActivityItems = [
    dStartDate && {
      key: "started",
      label: "Started",
      value: formatBookDate(dStartDate),
    },
    dEndDate && {
      key: "finished",
      label: "Finished",
      value: formatBookDate(dEndDate),
    },
    daysRead !== null && {
      key: "duration",
      label: "Read In",
      value: `${daysRead} day${daysRead === 1 ? "" : "s"}`,
    },
  ].filter(Boolean);

  const readHistoryItems = (dReads || []).map((entry, index) => {
    const started = entry?.started || entry?.startDate || "";
    const finished = entry?.finished || entry?.endDate || entry?.date || "";
    const days =
      entry?.days !== undefined && entry?.days !== null
        ? Number(entry.days)
        : started && finished
          ? readingDays(started, finished)
          : null;

    return {
      key: `${started || finished || entry?.date || "reread"}-${index}`,
      label: `Reread #${index + 1}`,
      started,
      finished,
      days,
    };
  });

  const bookInfoItems = [
    (active.isbn13 || active.isbn10 || active.isbn) && {
      key: "isbn",
      label: "ISBN",
      value: active.isbn13 || active.isbn10 || active.isbn,
    },
    activePublishedDisplay && {
      key: "published",
      label: "Published on",
      value: activePublishedDisplay,
    },
    activeHasPages && {
      key: "pages",
      label: "Pages",
      value: Number(active.pages).toLocaleString(),
    },
    active.publisher &&
      active.publisher !== "--" && {
        key: "publisher",
        label: "Published by",
        value: active.publisher,
      },
    dEdition && {
      key: "edition",
      label: "Edition",
      value: dEdition,
    },
  ].filter(Boolean);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        background: T.bg,
      }}
    >
      <div
        ref={detailViewScrollRef}
        style={{ flex: 1, overflowY: "auto", padding: "0 16px 56px" }}
      >
        <section
          style={{
            margin: "0 -16px 8px",
            padding: "18px 16px 10px",
            background: T.bg,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {active.cover && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                overflow: "hidden",
              }}
            >
              <img
                src={active.cover}
                alt=""
                style={{
                  position: "absolute",
                  inset: "-26px",
                  width: "calc(100% + 52px)",
                  height: "calc(100% + 52px)",
                  objectFit: "cover",
                  objectPosition: "center 18%",
                  filter: `blur(${isDarkTheme ? 42 : 36}px) saturate(1.16)`,
                  transform: "scale(1.18)",
                  opacity: heroArtOpacity,
                }}
              />
            </div>
          )}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: heroGradient,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 10,
            }}
          >
            <button
              onClick={onBack}
              style={{
                background: "transparent",
                border: "none",
                padding: "6px 0",
                color: T.text,
                cursor: "pointer",
                fontSize: 13,
                fontFamily: SANS,
              }}
            >
              ← Back
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={onToggleFavorite}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                {dFav ? "❤️" : "🤍"}
              </button>
              <button
                onClick={onEdit}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: T.text,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: SANS,
                }}
              >
                Edit
              </button>
            </div>
          </div>

          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "flex-start",
              gap: 18,
            }}
          >
            <div style={{ flexShrink: 0, marginLeft: 5 }}>
              <BookCover book={active} w={107} h={163} r={10} />
            </div>

            <div
              style={{
                minWidth: 0,
                flex: 1,
                paddingTop: 6,
              }}
            >
              <div
                style={{
                  minHeight: 17,
                  marginBottom: 5,
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                {(dSeriesName || dEdition) && (
                  <div
                    style={{
                      fontSize: 10,
                      color: seriesLabelColor,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {[
                      dSeriesName &&
                        `${dSeriesName}${dSeriesNum ? ` #${dSeriesNum}` : ""}`,
                      dEdition,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
              </div>

              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontWeight: 600,
                  fontSize: 20,
                  color: T.text,
                  lineHeight: 1.12,
                  letterSpacing: "-0.02em",
                  marginBottom: 5,
                }}
              >
                {active.title}
              </div>

              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: T.sub,
                  lineHeight: 1.45,
                  marginBottom: 24,
                }}
              >
                {active.authors}
              </div>

              {(statusLabel || formatLabels.length > 0) && (
                <div
                  style={{
                    marginBottom: dRating > 0 ? 10 : 20,
                  }}
                >
                  {statusLabel && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: statusTone,
                        marginBottom: formatLabels.length > 0 ? 4 : 0,
                      }}
                    >
                      {statusLabel.toUpperCase()}
                    </div>
                  )}
                  {formatLabels.length > 0 && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        color: T.sub,
                        lineHeight: 1.5,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0,
                      }}
                    >
                      {formatLabels.map((label, index) => (
                        <span key={label}>
                          {index > 0 && (
                            <span
                              style={{
                                color: T.mute,
                                padding: "0 6px",
                              }}
                            >
                              ·
                            </span>
                          )}
                          <span>{String(label).toLowerCase()}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {dRating > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: -1,
                    marginBottom: fromNextUp ? 20 : 0,
                  }}
                >
                  <HalfStars val={dRating} size={15} />
                </div>
              )}
            </div>
          </div>
        </section>

        {(showPrimaryButton || fromNextUp) && (
          <div
            style={{
              marginBottom: 12,
            }}
          >
            {showPrimaryButton && (
              <button
                onClick={dStatus === "In Progress" ? onFinishReading : onStartReading}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "13px 18px",
                  borderRadius: 18,
                  ...outlineButtonStyle,
                  fontSize: 15,
                }}
              >
                {startReadingButtonLabel}
              </button>
            )}

            {fromNextUp && (
              <button
                onClick={onDelete}
                style={{
                  marginTop: showPrimaryButton ? 10 : 0,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "#C4584A",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Remove from Queue
              </button>
            )}
          </div>
        )}

        {(showInlineFinishButton || showInlineRereadFinishButton || (!showInlineFinishButton && readingActivityItems.length > 0)) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 22,
            }}
          >
            {showInlineFinishButton && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 18,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 14,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 15,
                      color: T.text,
                      lineHeight: 1.25,
                    }}
                  >
                    {formatBookDate(dStartDate)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: T.mute,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      marginTop: 4,
                      fontWeight: 700,
                    }}
                  >
                    Started
                  </div>
                </div>

                <button
                  onClick={onFinishReading}
                  style={{
                    flexShrink: 0,
                    padding: "11px 16px",
                    marginRight: 10,
                    ...outlineButtonStyle,
                  }}
                >
                  {startReadingButtonLabel}
                </button>
              </div>
            )}

            {showInlineRereadFinishButton && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 18,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 14,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 15,
                      color: T.text,
                      lineHeight: 1.25,
                    }}
                  >
                    {formatBookDate(dRereadStartDate)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: T.mute,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      marginTop: 4,
                      fontWeight: 700,
                    }}
                  >
                    Started Reread
                  </div>
                </div>

                <button
                  onClick={onFinishReading}
                  style={{
                    flexShrink: 0,
                    padding: "11px 16px",
                    marginRight: 10,
                    ...outlineButtonStyle,
                  }}
                >
                  Finished
                </button>
              </div>
            )}

            {!showInlineFinishButton && readingActivityItems.length > 0 && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 18,
                  padding: "14px 18px",
                  display: "flex",
                  justifyContent: "space-around",
                  gap: 12,
                  textAlign: "center",
                }}
              >
                {readingActivityItems.map((item) => (
                  <div key={item.key} style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: DISPLAY_SERIF,
                        fontSize: 15,
                        color: T.text,
                        lineHeight: 1.25,
                      }}
                    >
                      {item.value}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: T.mute,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        marginTop: 4,
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tagItems.length > 0 && (
          <section style={{ marginBottom: 20, paddingTop: 10 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: sectionLabelColor,
                marginBottom: 8,
              }}
            >
              Genre & Vibes
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.8,
                display: "flex",
                flexWrap: "wrap",
                gap: "2px 8px",
              }}
            >
              {visibleTagItems.map((item, index) => {
                const tone =
                  item.kind === "genre"
                    ? genreTone
                    : item.kind === "vibe"
                      ? vibeTone
                      : tagTone;
                const displayLabel =
                  item.kind === "tag"
                    ? `#${String(item.label).toLowerCase()}`
                    : String(item.label).toLowerCase();
                const needsSeparator = index < visibleTagItems.length - 1;

                return (
                  <span
                    key={`${item.kind}-${item.label}-${index}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        color: tone,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {displayLabel}
                    </span>
                    {needsSeparator && (
                      <span
                        style={{
                          color: separatorTone,
                          fontSize: 14,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        ·
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </section>
        )}

        {(dNotes || aboutText) && (
          <div style={{ marginBottom: 24 }}>
            {dNotes && (
              <section
                style={{
                  padding: "0 0 16px 14px",
                  borderLeft: `2px solid ${noteRuleColor}`,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: sectionLabelColor,
                    marginBottom: 10,
                  }}
                >
                  My Notes...
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: T.text,
                    lineHeight: 1.8,
                    fontStyle: "italic",
                    margin: 0,
                  }}
                >
                  "{dNotes}"
                </p>
              </section>
            )}

            {aboutText && (
              <section
                style={{
                  marginTop: dNotes ? 14 : 0,
                  padding: `${dNotes ? "18px" : "0"} 0 0 0`,
                  borderTop: dNotes ? `1px solid ${dividerColor}` : "none",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: sectionLabelColor,
                    marginBottom: 10,
                  }}
                >
                  About
                </div>
                <div
                  style={{ margin: 0 }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: T.sub,
                      lineHeight: 1.95,
                      margin: 0,
                    }}
                  >
                    {aboutText}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}

        {readHistoryItems.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: sectionLabelColor,
                marginBottom: 10,
              }}
            >
              Re-reads
            </div>
            {readHistoryItems.map((r, i) => (
              <div
                key={r.key}
                style={{
                  padding: i === 0 ? "0 0 12px" : "12px 0 0",
                  marginTop: i === 0 ? 0 : 12,
                  borderTop: i === 0 ? "none" : `1px solid ${dividerColor}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <div style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>
                  {r.label}
                </div>
                {r.started ? (
                  <>
                    <div style={{ fontSize: 12, color: T.sub }}>
                      Started {formatBookDate(r.started)}
                    </div>
                    <div style={{ fontSize: 12, color: T.mute }}>
                      Finished {formatBookDate(r.finished)}
                      {r.days !== null && r.days !== undefined
                        ? ` · ${r.days} day${r.days === 1 ? "" : "s"}`
                        : ""}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: T.mute }}>
                    {formatBookDate(r.finished)}
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        <section style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: sectionLabelColor,
              marginBottom: 10,
            }}
          >
            Book Info
          </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {bookInfoItems.length > 0 ? (
                bookInfoItems.map((item, index) => (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "flex-start",
                    gap: 8,
                    padding: index === bookInfoItems.length - 1 ? "0" : "0 0 10px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: sectionLabelColor,
                      flexShrink: 0,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    title={item.value}
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: T.sub,
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </div>
                </div>
                ))
              ) : (
                <div
                  style={{
                    fontSize: 14,
                    color: T.mute,
                    lineHeight: 1.5,
                  }}
                >
                  Add ISBN, published date, publisher, or page count.
                </div>
              )}
            </div>
        </section>

        <div style={{ paddingTop: 8 }}>
          <button
            onClick={onReportMetadata}
            style={{
              width: "100%",
              background: T.surface,
              border: `1px solid ${dividerColor}`,
              borderRadius: 14,
              padding: "12px 16px",
              color: T.text,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: SANS,
            }}
          >
            Fix Book Info
          </button>
          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              color: T.sub,
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            Wrong cover, title, or author? Send it for review.
          </div>
        </div>
      </div>
    </div>
  );
}
